import I18n from 'i18n-js';
import tenantTimeZones from '../../../helpers/tenantTimeZones';

const tenantFlag = column => ({
  read: {section: 'tenant_settings', field: column},
  write: {endpoint: 'tenant_settings', field: column}
});

// GET /settings names the plan limits num_users and friends; PUT /plan_limits only accepts max_users and friends
// and answers 422 "Unknown setting keys" to the read names. is_multi_box, is_box_champ and orders_delete_days are
// read from tenant_settings but are only writable through plan_limits (GROOV-5182).
const planLimit = (readField, writeField) => ({
  read: {section: 'plan_limits', field: readField},
  write: {endpoint: 'plan_limits', field: writeField}
});

const tenantColumnViaPlanLimits = column => ({
  read: {section: 'tenant_settings', field: column},
  write: {endpoint: 'plan_limits', field: column}
});

const generalSetting = column => ({
  read: {section: 'general', field: column},
  write: {endpoint: 'general_settings', field: column}
});

const scanPackSetting = column => ({
  read: {section: 'scan_pack', field: column},
  write: {endpoint: 'scan_pack_settings', field: column}
});

const composite = (composedBy, fields) => ({
  composite: composedBy,
  type: 'custom',
  fields,
  read: {section: 'general', fields},
  write: {endpoint: 'general_settings', fields}
});

const showValue = path => ({show: path});

const addon = feature => ({
  composite: 'addon',
  type: 'custom',
  show: ['tenant', 'basicinfo', 'price', feature],
  legacy: {kind: 'price', feature}
});

const basicinfoFlag = (field, kind, endpoint) => ({
  type: 'toggle',
  show: ['tenant', 'basicinfo', field],
  legacy: endpoint ? {kind, field, endpoint} : {kind, field}
});

const WORKFLOWS = [
  ['default', 'scanWorkflowDefault'],
  ['product_first_scan_to_put_wall', 'scanWorkflowProductFirst'],
  ['multi_put_wall', 'scanWorkflowMultiPutWall'],
  ['pick_tote', 'scanWorkflowPickTote'],
  ['scan_to_cart', 'scanWorkflowScanToCart']
];

export const GROUPS = {
  planBilling: {
    title: 'adminTenantPage.groupPlanBilling',
    description: 'adminTenantPage.groupPlanBillingDescription'
  },
  inventoryImport: {
    title: 'adminTenantPage.groupInventoryImport',
    description: 'adminTools.movedFromCustomerLine',
    badge: 'adminTools.movedFromCustomer'
  },
  workflow: {
    title: 'adminTenantPage.groupWorkflow',
    description: 'adminTenantPage.groupWorkflowDescription'
  },
  features: {
    title: 'adminTenantPage.groupFeatures',
    description: 'adminTenantPage.groupFeaturesDescription'
  },
  importUtilities: {
    title: 'adminTenantPage.groupImportUtilities',
    description: 'adminTenantPage.groupImportUtilitiesDescription'
  },
  logging: {
    title: 'adminTenantPage.groupLogging',
    description: 'adminTenantPage.groupLoggingDescription'
  },
  inventoryPush: {
    title: 'adminTenantPage.groupInventoryPush',
    description: 'adminTenantPage.groupInventoryPushDescription'
  },
  general: {
    title: 'customerSettings.groupGeneral',
    description: 'customerSettings.groupGeneralDescription'
  },
  scanPack: {
    title: 'customerSettings.groupScanPack',
    description: 'customerSettings.groupScanPackDescription'
  },
  printing: {
    title: 'customerSettings.groupPrinting',
    description: 'customerSettings.groupPrintingDescription'
  },
  stores: {title: 'customerSettings.groupStores'},
  users: {title: 'customerSettings.groupUsers'}
};

const TEAM_ROWS = [
  {
    key: 'plan',
    groupId: 'planBilling',
    type: 'text',
    locked: true,
    ...showValue(['tenant', 'subscription_info', 'plan'])
  },
  {
    key: 'regularUsers',
    groupId: 'planBilling',
    type: 'number',
    min: 0,
    ...planLimit('num_users', 'max_users')
  },
  {
    key: 'adminOnlyUsers',
    groupId: 'planBilling',
    type: 'number',
    min: 0,
    ...planLimit('administrative_users', 'max_administrative_users')
  },
  {
    key: 'shipmentsPerMonth',
    groupId: 'planBilling',
    type: 'number',
    min: 0,
    ...planLimit('num_shipments', 'max_allowed')
  },
  {
    key: 'deleteOrdersAfterDays',
    groupId: 'planBilling',
    type: 'number',
    min: 0,
    ...tenantColumnViaPlanLimits('orders_delete_days')
  },
  {key: 'highSku', groupId: 'planBilling', ...addon('high_sku_feature')},
  {
    key: 'doubleHighSku',
    groupId: 'planBilling',
    ...addon('double_high_sku')
  },
  {
    key: 'custMaintenance1',
    groupId: 'planBilling',
    ...addon('cust_maintenance_1')
  },
  {
    key: 'custMaintenance2',
    groupId: 'planBilling',
    ...addon('cust_maintenance_2')
  },
  {
    key: 'stripeCustomerId',
    groupId: 'planBilling',
    type: 'text',
    locked: true,
    gpadminOnly: true,
    ...showValue(['tenant', 'subscription_info', 'customer_id'])
  },
  {
    key: 'stripeSubscriptionId',
    groupId: 'planBilling',
    type: 'text',
    locked: true,
    gpadminOnly: true,
    ...showValue(['tenant', 'subscription_info', 'customer_subscription_id'])
  },

  {
    key: 'inventoryTracking',
    groupId: 'inventoryImport',
    type: 'toggle',
    ...generalSetting('inventory_tracking')
  },
  {
    key: 'generateNumericBarcodes',
    groupId: 'inventoryImport',
    ...composite('barcodes', ['barcode_length', 'starting_value'])
  },
  {
    key: 'removeZeroQuantityItems',
    groupId: 'inventoryImport',
    type: 'toggle',
    ...generalSetting('remove_order_items')
  },
  {
    key: 'preDayImportSchedule',
    groupId: 'inventoryImport',
    ...composite('schedule', [
      'scheduled_order_import',
      'time_to_import_orders',
      'schedule_import_mode'
    ])
  },
  {
    key: 'recurringFtpImportRange',
    groupId: 'inventoryImport',
    ...composite('ftpRange', ['from_import', 'to_import'])
  },

  {
    key: 'multiBox',
    groupId: 'features',
    type: 'toggle',
    ...tenantColumnViaPlanLimits('is_multi_box')
  },
  {
    key: 'boxChamp',
    groupId: 'features',
    type: 'toggle',
    ...tenantColumnViaPlanLimits('is_box_champ')
  },
  {
    key: 'amazonFba',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('is_fba')
  },
  {
    key: 'realTimePackerStats',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('allow_rts')
  },
  {
    key: 'hourlyOrderImport',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('scheduled_import_toggle')
  },
  {
    key: 'productFtpImport',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('product_ftp_import')
  },
  {
    key: 'inventoryReportOption',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('inventory_report_toggle')
  },
  {
    key: 'postScanningApiCall',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('api_call')
  },
  {
    key: 'groovelyticsStats',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('groovelytic_stat')
  },
  {
    key: 'enableDeveloperTools',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('enable_developer_tools')
  },
  {
    key: 'dailyPackedPercent',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('daily_packed_toggle')
  },
  {
    key: 'postImportRulesEngine',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('rules_engine_enabled')
  },
  {
    key: 'packingCam',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('packing_cam')
  },
  {
    key: 'voicePacking',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('voice_packing')
  },
  {
    key: 'newScanPackInterface',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('use_scanpack_scanning_v2')
  },
  {
    key: 'scanToScore',
    groupId: 'features',
    type: 'toggle',
    ...tenantFlag('scan_to_score')
  },

  {
    key: 'scanWorkflow',
    groupId: 'workflow',
    type: 'select',
    fallback: WORKFLOWS[0][0],
    show: ['tenant', 'basicinfo', 'scan_pack_workflow'],
    legacy: {kind: 'workflow'}
  },
  {
    key: 'postScanningOptionsV2',
    groupId: 'workflow',
    type: 'toggle',
    read: {section: 'post_scanning_config', field: 'psv2_enabled'},
    legacy: {kind: 'psv2'}
  },
  {
    key: 'shopifyGraphqlOrderImport',
    groupId: 'workflow',
    needsShopify: true,
    ...basicinfoFlag('use_graphql_order_import', 'graphql')
  },
  {
    key: 'shopifyGraphqlProductImport',
    groupId: 'workflow',
    needsShopify: true,
    ...basicinfoFlag('use_graphql_product_import', 'graphql')
  },
  {
    key: 'shopifyGraphqlWrites',
    groupId: 'workflow',
    needsShopify: true,
    ...basicinfoFlag('use_graphql_writes', 'graphql')
  },
  {
    key: 'shopifyTrackingLookup',
    groupId: 'workflow',
    needsShopify: true,
    ...basicinfoFlag(
      'tracking_lookup_enabled',
      'graphql',
      'update_tracking_lookup'
    )
  },

  {
    key: 'shipworksImportWithDelay',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('is_delay')
  },
  {
    key: 'inventoryUpdateWithDelay',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('delayed_inventory_update')
  },
  {
    key: 'showExternalLogsButton',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('show_external_logs_button')
  },
  {
    key: 'orderCupDirectShipping',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('order_cup_direct_shipping')
  },
  {
    key: 'getOrderResponseLog',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('store_order_respose_log')
  },
  {
    key: 'gdprShipStation',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('gdpr_shipstation')
  },
  {
    key: 'uniqueShopifyImport',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('uniq_shopify_import')
  },
  {
    key: 'showOriginatingStoreId',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('show_originating_store_id')
  },
  {
    key: 'enableMultiShipment',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('enable_multi_shipment')
  },
  {
    key: 'enableReprintOption',
    groupId: 'importUtilities',
    type: 'toggle',
    ...scanPackSetting('enable_reprint_option')
  },
  {
    key: 'useShipStationV2Api',
    groupId: 'importUtilities',
    type: 'toggle',
    ...tenantFlag('use_ss_v2_api')
  },

  {
    key: 'logglyShipworksImport',
    groupId: 'logging',
    type: 'toggle',
    ...tenantFlag('loggly_sw_imports')
  },
  {
    key: 'logglyShopifyImport',
    groupId: 'logging',
    type: 'toggle',
    ...tenantFlag('loggly_shopify_imports')
  },
  {
    key: 'logglyGpxPrintingLabel',
    groupId: 'logging',
    type: 'toggle',
    ...tenantFlag('loggly_gpx_print_label')
  },
  {
    key: 'logglyShippingEasyImport',
    groupId: 'logging',
    type: 'toggle',
    ...tenantFlag('loggly_se_imports')
  },
  {
    key: 'logglyShipStationImport',
    groupId: 'logging',
    type: 'toggle',
    ...tenantFlag('loggly_shipstation_imports')
  },
  {
    key: 'logglyVeeqoImport',
    groupId: 'logging',
    type: 'toggle',
    ...tenantFlag('loggly_veeqo_imports')
  },
  {
    key: 'logglyGpxOrderScan',
    groupId: 'logging',
    type: 'toggle',
    ...tenantFlag('loggly_gpx_order_scan')
  },
  {
    key: 'logglyGpScannedTag',
    groupId: 'logging',
    type: 'toggle',
    ...tenantFlag('loggly_gpscanned_tag')
  },
  {
    key: 'logglyProductInventoryTrack',
    groupId: 'logging',
    type: 'toggle',
    ...tenantFlag('loggly_product_inventory')
  },

  {
    key: 'inventoryPushBigCommerce',
    groupId: 'inventoryPush',
    type: 'toggle',
    ...planLimit('allow_bc_inv_push', 'allow_bc_inv_push')
  },
  {
    key: 'inventoryPushShopify',
    groupId: 'inventoryPush',
    type: 'toggle',
    ...planLimit('allow_shopify_inv_push', 'allow_shopify_inv_push')
  },
  {
    key: 'inventoryPushShopline',
    groupId: 'inventoryPush',
    type: 'toggle',
    ...planLimit('allow_shopline_inv_push', 'allow_shopline_inv_push')
  },
  {
    key: 'inventoryPushMagento2',
    groupId: 'inventoryPush',
    type: 'toggle',
    ...planLimit('allow_mg_rest_inv_push', 'allow_mg_rest_inv_push')
  },
  {
    key: 'inventoryPushTeapplix',
    groupId: 'inventoryPush',
    type: 'toggle',
    ...planLimit('allow_teapplix_inv_push', 'allow_teapplix_inv_push')
  }
];

const LABEL_SIZES = ['3 x 1', '2 x 1', '1.5 x 1'];

// packing_slip_size is a free string column on the backend, so these six values are the ones the webclient offers
const PACKING_SLIP_SIZES = [
  '4 x 6',
  '8.5 x 11',
  'Custom 4 x 6',
  'Custom 4 x 6 + Shipping',
  '4 x 4',
  '4 x 2'
];

const CUSTOMER_ROWS = [
  {
    key: 'timeZone',
    groupId: 'general',
    type: 'select',
    keepCurrentOption: true,
    ...generalSetting('new_time_zone')
  },
  {
    key: 'billingNotificationEmail',
    groupId: 'general',
    type: 'text',
    ...generalSetting('email_address_for_billing_notification')
  },
  {
    key: 'outOfStockReportEmail',
    groupId: 'general',
    type: 'text',
    ...generalSetting('email_address_for_report_out_of_stock')
  },
  {
    key: 'lowInventoryAlertLimit',
    groupId: 'general',
    type: 'number',
    min: 1,
    ...generalSetting('default_low_inventory_alert_limit')
  },
  {
    key: 'includeActionRequiredOrders',
    groupId: 'scanPack',
    type: 'toggle',
    ...scanPackSetting('include_action_required_orders')
  },
  {
    key: 'suppressSoftwareKeyboard',
    groupId: 'scanPack',
    type: 'toggle',
    ...scanPackSetting('suppress_software_keyboard')
  },
  {
    key: 'boxScanVerification',
    groupId: 'scanPack',
    type: 'toggle',
    ...scanPackSetting('box_scan_verification')
  },
  {
    key: 'orderNumberFallbackPrefixes',
    groupId: 'scanPack',
    type: 'text',
    ...scanPackSetting('order_num_fallback_prefixes')
  },
  {
    key: 'toteIdentifier',
    groupId: 'scanPack',
    type: 'text',
    ...scanPackSetting('tote_identifier')
  },
  {
    key: 'productBarcodeLabelSize',
    groupId: 'printing',
    type: 'select',
    read: {section: 'printing', field: 'product_barcode_label_size'},
    write: {endpoint: 'general_settings', field: 'product_barcode_label_size'}
  },
  {
    key: 'packingSlipSize',
    groupId: 'printing',
    type: 'select',
    keepCurrentOption: true,
    ...generalSetting('packing_slip_size')
  }
];

const LIST_ROWS = [
  {key: 'stores', groupId: 'stores', composite: 'stores'},
  {key: 'users', groupId: 'users', composite: 'users'}
];

const MAX_TOTES_PREFIX = 'maxTotes:';

const ALL_ROWS = [...TEAM_ROWS, ...CUSTOMER_ROWS];

const rowFor = key => ALL_ROWS.find(row => row.key === key);

const pick = (source, fields) =>
  fields.reduce((picked, field) => {
    picked[field] = source ? source[field] : undefined;
    return picked;
  }, {});

const readShow = (show, path) =>
  path.reduce((node, part) => (node == null ? undefined : node[part]), show);

const readRow = (row, settings, show) => {
  if (row.show) {
    const value = readShow(show, row.show);
    if (row.type === 'toggle') return Boolean(value);
    return value || row.fallback || value;
  }
  const section = settings && settings[row.read.section];
  if (row.read.fields) return pick(section, row.read.fields);
  const value = section ? section[row.read.field] : undefined;
  return row.type === 'toggle' ? Boolean(value) : value;
};

const toteSetsOf = settings =>
  (settings && settings.scan_pack && settings.scan_pack.tote_sets) || [];

const listOf = (settings, name) =>
  settings && Array.isArray(settings[name]) ? settings[name] : null;

const usersValue = settings => {
  const rows = listOf(settings, 'users');
  if (!rows) return null;
  const counts = (settings && settings.user_counts) || {};
  const limits = (settings && settings.plan_limits) || {};
  return {
    rows,
    regular: counts.regular,
    administrative: counts.administrative,
    regularLimit: limits.num_users,
    administrativeLimit: limits.administrative_users
  };
};

export const readValues = (settings, show) => {
  const values = {};
  ALL_ROWS.forEach(row => {
    values[row.key] = readRow(row, settings, show);
  });
  values.stores = listOf(settings, 'stores');
  values.users = usersValue(settings);
  toteSetsOf(settings).forEach(set => {
    values[`${MAX_TOTES_PREFIX}${set.id}`] = set.max_totes;
  });
  if (toteSetsOf(settings).length === 1) {
    values.maxTotes = toteSetsOf(settings)[0].max_totes;
  }
  return values;
};

const label = key => I18n.t(`tenantSettings.${key}`);
const tooltip = key => I18n.t(`tenantSettings.${key}Tooltip`);
const customerLabel = (key, options) =>
  I18n.t(`customerSettings.${key}`, options);
const customerTooltip = key => I18n.t(`customerSettings.${key}Tooltip`);

const asOptions = values => values.map(value => ({label: value, value}));

const withCurrentOption = (options, current) =>
  current && !options.some(option => option.value === current)
    ? [...options, {label: current, value: current}]
    : options;

const workflowOptions = () =>
  WORKFLOWS.map(([value, key]) => ({value, label: label(key)}));

const optionsFor = (row, settings) => {
  let options;
  if (row.key === 'scanWorkflow') options = workflowOptions();
  if (row.key === 'timeZone') options = asOptions(tenantTimeZones);
  if (row.key === 'productBarcodeLabelSize') options = asOptions(LABEL_SIZES);
  if (row.key === 'packingSlipSize') options = asOptions(PACKING_SLIP_SIZES);
  if (!options || !row.keepCurrentOption) return options;
  return withCurrentOption(options, readRow(row, settings, undefined));
};

const NO_SHOPIFY_STORE_REASON = () => I18n.t('adminTenantPage.noShopifyStore');

const definitionOf = (row, band, text, settings, shopifyConnected = true) => ({
  key: row.key,
  group: I18n.t(GROUPS[row.groupId].title),
  groupId: row.groupId,
  label: text.label(row.key),
  tooltip: text.tooltip(row.key),
  type: row.type,
  band,
  composite: row.composite,
  min: row.min,
  max: row.max,
  locked: row.locked,
  disabled: row.needsShopify ? !shopifyConnected : undefined,
  disabledReason:
    row.needsShopify && !shopifyConnected
      ? NO_SHOPIFY_STORE_REASON()
      : undefined,
  options: optionsFor(row, settings)
});

const groupMetaFor = definitions => {
  const meta = {};
  definitions.forEach(({groupId}) => {
    if (meta[groupId]) return;
    const group = GROUPS[groupId];
    meta[groupId] = {
      title: I18n.t(group.title),
      description: group.description ? I18n.t(group.description) : undefined,
      badge: group.badge ? I18n.t(group.badge) : undefined
    };
  });
  return meta;
};

export const buildTeamDefinitions = ({
  isGpadmin = false,
  hasShopifyStore = false
} = {}) => {
  const definitions = TEAM_ROWS.filter(
    row => isGpadmin || !row.gpadminOnly
  ).map(row =>
    definitionOf(row, 'team', {label, tooltip}, undefined, hasShopifyStore)
  );
  return {definitions, groupMeta: groupMetaFor(definitions)};
};

const storesMeta = settings => {
  const stores = listOf(settings, 'stores');
  if (!stores) return {};
  return {
    description: I18n.t('customerSettings.groupStoresDescription', {
      count: stores.length
    }),
    countLabel: String(stores.length)
  };
};

const shown = value => (value === null || value === undefined ? '—' : value);

export const userPillTexts = users => [
  I18n.t('customerSettings.usersPillRegular', {
    count: shown(users.regular),
    limit: shown(users.regularLimit)
  }),
  I18n.t('customerSettings.usersPillAdminOnly', {
    count: shown(users.administrative),
    limit: shown(users.administrativeLimit)
  })
];

const usersMeta = settings => {
  const users = usersValue(settings);
  if (!users) return {};
  return {
    description: I18n.t('customerSettings.groupUsersDescription', {
      regular: shown(users.regular),
      regularLimit: shown(users.regularLimit),
      admin: shown(users.administrative),
      adminLimit: shown(users.administrativeLimit)
    }),
    countLabel: userPillTexts(users).join(' · ')
  };
};

export const buildCustomerDefinitions = ({settings} = {}) => {
  const text = {label: customerLabel, tooltip: customerTooltip};
  const definitions = CUSTOMER_ROWS.map(row =>
    definitionOf(row, 'customer', text, settings)
  );
  const sets = toteSetsOf(settings);
  sets.forEach(set => {
    const single = sets.length === 1;
    definitions.push({
      key: single ? 'maxTotes' : `${MAX_TOTES_PREFIX}${set.id}`,
      group: I18n.t(GROUPS.scanPack.title),
      groupId: 'scanPack',
      label: single
        ? customerLabel('maxTotes')
        : I18n.t('customerSettings.maxTotesForSet', {
            defaultValue: 'Maximum totes: {{name}}',
            name: set.name
          }),
      tooltip: customerTooltip('maxTotes'),
      type: 'number',
      band: 'customer',
      min: 1,
      max: 1000
    });
  });
  LIST_ROWS.forEach(row => {
    definitions.push({
      key: row.key,
      group: I18n.t(GROUPS[row.groupId].title),
      groupId: row.groupId,
      label: I18n.t(GROUPS[row.groupId].title),
      type: 'custom',
      band: 'customer',
      composite: row.composite
    });
  });
  const groupMeta = groupMetaFor(definitions);
  Object.assign(groupMeta.stores, storesMeta(settings));
  Object.assign(groupMeta.users, usersMeta(settings));
  return {definitions, groupMeta};
};

const normalizedValue = (row, value) => {
  if (row.write.fields) return pick(value, row.write.fields);
  return {[row.write.field]: value};
};

export const buildWriteRequest = (settingKey, value, settings) => {
  if (settingKey === 'maxTotes' || settingKey.indexOf(MAX_TOTES_PREFIX) === 0) {
    const sets = toteSetsOf(settings);
    const id =
      settingKey === 'maxTotes'
        ? sets.length === 1 && sets[0].id
        : Number(settingKey.slice(MAX_TOTES_PREFIX.length));
    if (!id) return null;
    return {
      endpoint: 'scan_pack_settings',
      body: {tote_sets: [{id, max_totes: value}]},
      sent: {}
    };
  }
  const row = rowFor(settingKey);
  if (!row || !row.write) return null;
  const sent = normalizedValue(row, value);
  return {endpoint: row.write.endpoint, body: {settings: sent}, sent};
};

const readTargets = () => {
  const targets = {};
  ALL_ROWS.forEach(row => {
    if (!row.write) return;
    const writeFields = row.write.fields || [row.write.field];
    const readFields = row.read.fields || [row.read.field];
    writeFields.forEach((field, index) => {
      targets[`${row.write.endpoint}:${field}`] = {
        section: row.read.section,
        field: readFields[index]
      };
    });
  });
  return targets;
};

const READ_TARGETS = readTargets();

export const buildSettingsPatch = (
  request,
  responseBody,
  settings,
  sideEffects = []
) => {
  const patch = {};
  const merged = {
    ...request.sent,
    ...((responseBody && responseBody.settings) || {})
  };
  Object.keys(merged).forEach(field => {
    const target = READ_TARGETS[`${request.endpoint}:${field}`];
    if (!target) return;
    patch[target.section] = patch[target.section] || {};
    patch[target.section][target.field] = merged[field];
  });
  if (sideEffects.includes('schedule_import_mode_reset')) {
    patch.general = {...patch.general, schedule_import_mode: 'Daily'};
  }
  const totes = responseBody && responseBody.tote_sets;
  if (Array.isArray(totes) && totes.length > 0) {
    const byId = new Map(toteSetsOf(settings).map(set => [set.id, set]));
    totes.forEach(set => byId.set(set.id, {...byId.get(set.id), ...set}));
    patch.scan_pack = {
      ...patch.scan_pack,
      tote_sets: Array.from(byId.values())
    };
  }
  return patch;
};

export const legacyRouteOf = settingKey => {
  const row = rowFor(settingKey);
  return row && row.legacy ? row.legacy : null;
};

export const writeEndpointOf = settingKey => {
  const row = rowFor(settingKey);
  return row && row.write ? row.write.endpoint : null;
};

export const teamKeys = () => TEAM_ROWS.map(row => row.key);
export const customerKeys = () => CUSTOMER_ROWS.map(row => row.key);
export const writeRouteKeys = () =>
  ALL_ROWS.filter(row => row.write).map(row => row.key);
